import { Injectable } from '@angular/core';

export interface PackagePersoDraft<TState> {
  key: string;
  savedAt: Date;
  state: TState;
  files: Record<string, File>;
}

/**
 * Conserve localement l'état du formulaire de création de paquet perso :
 * les valeurs sérialisables dans localStorage, les fichiers sélectionnés dans IndexedDB.
 */
@Injectable({
  providedIn: 'root'
})
export class PackagePersoDraftService {
  private readonly storagePrefix = 'packagePerso.draft.';
  private readonly databaseName = 'packagePersoDrafts';
  private readonly databaseVersion = 1;
  private readonly storeName = 'files';

  buildKey(utilisateurId: number, packageId: number | null): string {
    return `${this.storagePrefix}${utilisateurId}.${packageId ?? 'nouveau'}`;
  }

  async save<TState>(
    key: string,
    state: TState,
    files: Record<string, File>
  ): Promise<Date | null> {
    const savedAt = new Date();

    try {
      localStorage.setItem(key, JSON.stringify({ savedAt: savedAt.toISOString(), state }));
    } catch (exc) {
      console.error('Sauvegarde locale du brouillon impossible:', exc);

      return null;
    }

    await this.saveFiles(key, files);

    return savedAt;
  }

  async get<TState>(key: string): Promise<PackagePersoDraft<TState> | null> {
    let stored: string | null = null;

    try {
      stored = localStorage.getItem(key);
    } catch (exc) {
      console.error('Lecture du brouillon local impossible:', exc);

      return null;
    }

    if (stored === null) {
      return null;
    }

    try {
      const parsed = JSON.parse(stored) as { savedAt: string; state: TState };
      const files = await this.getFiles(key);

      return {
        key,
        savedAt: new Date(parsed.savedAt),
        state: parsed.state,
        files
      };
    } catch (exc) {
      console.error('Brouillon local illisible, il est supprimé:', exc);
      await this.delete(key);

      return null;
    }
  }

  async delete(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (exc) {
      console.error('Suppression du brouillon local impossible:', exc);
    }

    await this.deleteFiles(key);
  }

  /** Évite l'accumulation de brouillons abandonnés (et des fichiers qui les accompagnent). */
  async purgeExpired(maxAgeInMs: number): Promise<void> {
    const expiredKeys: string[] = [];
    const limit = Date.now() - maxAgeInMs;

    try {
      for (let index = 0; index < localStorage.length; index++) {
        const key = localStorage.key(index);

        if (key === null || !key.startsWith(this.storagePrefix)) {
          continue;
        }

        const stored = localStorage.getItem(key);
        const savedAt = stored === null ? null : (JSON.parse(stored) as { savedAt: string }).savedAt;

        if (savedAt === null || new Date(savedAt).getTime() < limit) {
          expiredKeys.push(key);
        }
      }
    } catch (exc) {
      console.error('Purge des brouillons locaux impossible:', exc);

      return;
    }

    for (const key of expiredKeys) {
      await this.delete(key);
    }
  }

  private openDatabase(): Promise<IDBDatabase | null> {
    return new Promise<IDBDatabase | null>(resolve => {
      if (typeof indexedDB === 'undefined') {
        resolve(null);

        return;
      }

      const request = indexedDB.open(this.databaseName, this.databaseVersion);

      request.onupgradeneeded = () => {
        const database = request.result;

        if (!database.objectStoreNames.contains(this.storeName)) {
          database.createObjectStore(this.storeName);
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      request.onblocked = () => resolve(null);
    });
  }

  private async saveFiles(
    key: string,
    files: Record<string, File>
  ): Promise<void> {
    const database = await this.openDatabase();

    if (database === null) {
      return;
    }

    await new Promise<void>(resolve => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);

      if (Object.keys(files).length === 0) {
        store.delete(key);
      } else {
        store.put(files, key);
      }

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });

    database.close();
  }

  private async getFiles(key: string): Promise<Record<string, File>> {
    const database = await this.openDatabase();

    if (database === null) {
      return {};
    }

    const files = await new Promise<Record<string, File>>(resolve => {
      const transaction = database.transaction(this.storeName, 'readonly');
      const request = transaction.objectStore(this.storeName).get(key);

      request.onsuccess = () => resolve((request.result as Record<string, File>) ?? {});
      request.onerror = () => resolve({});
    });

    database.close();

    return files;
  }

  private async deleteFiles(key: string): Promise<void> {
    const database = await this.openDatabase();

    if (database === null) {
      return;
    }

    await new Promise<void>(resolve => {
      const transaction = database.transaction(this.storeName, 'readwrite');
      transaction.objectStore(this.storeName).delete(key);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      transaction.onabort = () => resolve();
    });

    database.close();
  }
}
